import React, { useCallback, useMemo } from 'react';
import {
  DefaultValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { startOfToday, parse, formatISO } from 'date-fns';
import { toast } from 'react-toastify';
import { BuslineDetailQueryResponse } from '@area-restrita/api-client/model/buslineDetailQueryResponse';
import { useUpdateBusline } from '@area-restrita/api-client/endpoints/buslines/buslines.gen';

import { IBuslineSchema, buslineSchema } from '../../components/BuslineForm';
import BuslineGeneralView from './view';

interface IProps {
  busline: BuslineDetailQueryResponse;
}

const BuslineGeneral: React.FC<IProps> = ({ busline }) => {
  const defaultValues = useMemo<DefaultValues<IBuslineSchema>>(
    () => ({
      name: busline.name,
      code: busline.code,
      checkList: busline.checklist && {
        value: busline.checklist.id,
        label: busline.checklist.name,
      },
      company: busline.company && {
        value: busline.company.id,
        label: busline.company.tradingName,
      },
      driver: busline.driver && {
        value: busline.driver.id,
        label: busline.driver.fullName,
      },
      fleet: busline.fleet && {
        value: busline.fleet.id,
        label: busline.fleet.prefix,
      },
      startAt: busline.schedules.at(0)?.startAt
        ? parse(busline.schedules[0]?.startAt, 'HH:mm:ss', startOfToday())
        : undefined,
      endAt: busline.schedules.at(0)?.endAt
        ? parse(busline.schedules[0]?.endAt, 'HH:mm:ss', startOfToday())
        : undefined,
      daysOfWeek: busline.schedules.at(0)?.daysOfWeek
        ? busline.schedules[0]?.daysOfWeek
        : [],
    }),
    [busline],
  );

  const form = useForm<IBuslineSchema>({
    defaultValues,
    resolver: zodResolver(buslineSchema),
  });

  const { mutateAsync } = useUpdateBusline({
    mutation: {
      onSuccess: () => {
        toast.success('Rota editada com sucesso');
      },
      onError: () => {
        toast.error('Erro ao editar rota');
      },
    },
  });

  const onSubmit = useCallback<SubmitHandler<IBuslineSchema>>(
    async ({
      company,
      fleet,
      checkList,
      startAt,
      endAt,
      daysOfWeek,
      lineStrings,
      lineStringSelected,
      ...data
    }) => {
      await mutateAsync({
        id: busline.id,
        data: {
          fleetId: fleet.value,
          checklistId: checkList?.value,
          companyId: company.value,
          driverId: data.driver?.value,
          route:
            lineStringSelected && lineStrings
              ? lineStrings.at(lineStringSelected.value) || undefined
              : undefined,
          schedules: [
            {
              startAt: formatISO(startAt).split('T')[1].slice(0, -1),
              endAt: formatISO(endAt).split('T')[1].slice(0, -1),
              daysOfWeek,
            },
          ],
          ...data,
        },
      });
    },
    [busline, mutateAsync],
  );

  return (
    <FormProvider {...form}>
      <BuslineGeneralView
        busline={busline}
        control={form.control}
        isDirty={form.formState.isDirty}
        isLoading={form.formState.isSubmitting}
        onReset={() => form.reset()}
        onSubmit={form.handleSubmit(onSubmit)}
      />
    </FormProvider>
  );
};

export default BuslineGeneral;
